import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import useAuthStore from '../store/useAuthStore';
import kycService from '../services/kycService';
import '../components/layout/Header.css';
const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading, error } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  //const from = location.state?.from?.pathname || '/trade';
  //const from = location.state?.from?.pathname || '/swap';
  const from = location.state?.from?.pathname || '/';

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const checkAndRedirectKYC = async () => {
    try {
      // Check if user has already been prompted (using sessionStorage to persist during session only)
      const kycPromptShown = sessionStorage.getItem('kyc_prompt_shown');
      
      if (kycPromptShown) {
        // User already saw the prompt this session, go to intended destination
        navigate(from, { replace: true });
        return;
      }

      // Check KYC status
      const response = await kycService.checkKYCStatus();
      
      if (response.kyc_answer === 'GREEN') {
        // User is verified, proceed normally
        navigate(from, { replace: true });
      } else if (!response.kyc_answer || response.kyc_answer === null || response.kyc_answer === undefined || response.kyc_answer === '') {
        // User has not submitted KYC (null, undefined, empty string), show prompt
        navigate('/kyc?prompt=true', { replace: true });
      } else {
        // User has submitted but pending/rejected, proceed normally
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error('Error checking KYC:', error);
      // If KYC check fails, proceed to intended destination
      navigate(from, { replace: true });
    }
  };

  const onSubmit = async (data) => {
    try {
      await login(data);
      toast.success('Login successful!');
      // Check KYC status before redirecting
      await checkAndRedirectKYC();
    } catch (error) {
      // The error handling is now done in the service layer, so we can use the error message directly
      const errorMessage = typeof error === 'string' ? error : (error.message || 'Login failed');
      if (errorMessage === 'email_not_verified') {
        toast('Please verify your email to continue.');
        navigate('/verify-otp', { state: { email: data.email } });
        return;
      }
      toast.error(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center sm:px-6 lg:px-8">

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">

          <h2 className="mt-6 text-center md:text-3xl text-2xl font-extrabold  dark:text-white">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-white dark:text-white">
            Or{' '}
            <Link
              to="/register"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              create a new account
            </Link>
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card card-body">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            <div className='w-full'>
              <label htmlFor="email" className="block text-sm font-medium text-white dark:text-white">
                Email address
              </label>
              <div className="mt-1 w-full">
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  type="email"
                  autoComplete="email"
                  className="w-full"
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.email.message}
                  </p>
                )}
              </div>
            </div>

            <div className='w-full'>
              <label htmlFor="password" className="block text-sm font-medium text-white dark:text-white">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="w-full"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-white" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-white" />
                  )}
                </button>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.password.message}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-white dark:text-white">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2 rounded-full disabled:opacity-50 !text-white !bg-[#0E1696] disabled:cursor-not-allowed flex justify-center items-center"
              >
                {isLoading ? (
                  <>
                    <div className="loading-spinner mr-2"></div>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-white dark:text-white">
                  New to P2P1?
                </span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/register"
                className="w-full bg-primary-600 text-white p-2 rounded-full hover:bg-primary-700 block"
              >
                Create your account
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 